import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import {
  STATUS_LABEL, applyExtracted, calcInv, fmtD, fmtYM, money, num2, parseAmount, rateDateFor, taxPoint, type Invoice,
} from '@biuro/shared';
import { api, type ExtractResponse } from '../api.ts';
import { useData, useDeleteDoc, useMe, useToast } from '../hooks.tsx';
import { DeleteButton, Icon, Sheet } from '../ui.tsx';
import { ServiceForm } from './ServiceForm.tsx';
import { FileDrop } from '../FileDrop.tsx';

/** Wartość listy usług dla faktury „Inne” – w danych zapisuje się jako pusty serviceId. */
const OTHER = '__inne';

interface Form {
  serviceId: string;
  vendor: string;
  country: string;
  number: string;
  issueDate: string;
  periodFrom: string;
  periodTo: string;
  paidDate: string;
  tpManual: boolean;
  taxPoint: string;
  currency: string;
  amount: string;
  vatRate: string;
  rate: string;
  rateTable: string;
  status: NonNullable<Invoice['status']>;
  note: string;
}

const fmtRate = (x: number) => String(x).replace('.', ',');

/** Pola formularza, które można wypełnić z PDF, i ich nazwy w komunikacie. */
const PDF_FIELDS = ['number', 'issueDate', 'paidDate', 'periodFrom', 'periodTo', 'currency', 'amount', 'vendor', 'country'] as const;
type PdfField = (typeof PDF_FIELDS)[number];
const PDF_LABEL: Record<string, string> = {
  number: 'numer', issueDate: 'data wystawienia', paidDate: 'data zapłaty', periodFrom: 'okres', periodTo: 'okres',
  currency: 'waluta', amount: 'kwota', vatCharged: 'naliczony VAT', vendor: 'dostawca', country: 'kraj',
};
const isPdf = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name);

export function InvoiceForm({ id, onClose }: { id?: string; onClose: () => void }) {
  const { data } = useData();
  const me = useMe().data;
  const qc = useQueryClient();
  const toast = useToast();
  const del = useDeleteDoc();
  const initial = id ? data.invoices[id] : undefined;
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [rateState, setRateState] = useState<string>('');
  const [newService, setNewService] = useState(false);
  // Pola wypełnione z PDF (z importu Gmaila albo z odczytu w formularzu) – podświetlone do sprawdzenia.
  const [extracted, setExtracted] = useState<string[]>(() => initial?.extracted || []);
  const [vatCharged, setVatCharged] = useState<number | undefined>(undefined);
  const [pdfNote, setPdfNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [reading, setReading] = useState(false);

  const { register, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm<Form>({
    defaultValues: {
      serviceId: initial ? initial.serviceId || OTHER : '', vendor: initial?.vendor || '', country: initial?.country || '',
      number: initial?.number || '', issueDate: initial?.issueDate || '',
      periodFrom: initial?.periodFrom || '', periodTo: initial?.periodTo || '', paidDate: initial?.paidDate || '',
      tpManual: !!initial?.tpManual, taxPoint: initial?.tpManual ? initial.taxPoint || '' : '',
      currency: initial?.currency || '', amount: initial?.amount != null && initial.amount !== '' ? num2(Number(initial.amount)).replace(/\s/g, '') : '',
      vatRate: String(initial?.vatRate ?? 23), rate: initial?.rate ? fmtRate(initial.rate) : '', rateTable: initial?.rateTable || '',
      status: initial?.status || 'new', note: initial?.note || '',
    },
  });

  const v = watch();
  const other = v.serviceId === OTHER;
  const svc = other ? null : data.services[v.serviceId] || null;
  const cur = (v.currency || '').toUpperCase();

  // Waluta z usługi, gdy pole puste.
  useEffect(() => {
    if (svc?.currency && !getValues('currency')) setValue('currency', svc.currency);
    if (svc?.vatRate && !initial) setValue('vatRate', String(svc.vatRate));
  }, [v.serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formInv = (f: Form): Invoice => {
    const c3 = (f.currency || '').toUpperCase();
    return {
      ...(initial || {}),
      // „Inne”: dostawca i kraj z faktury; z usługą – dane z usługi, więc pola faktury znikają.
      ...(f.serviceId === OTHER
        ? { serviceId: '', vendor: f.vendor.trim(), country: f.country.trim().toUpperCase() }
        : { serviceId: f.serviceId, vendor: undefined, country: undefined }),
      number: f.number.trim(), issueDate: f.issueDate, periodFrom: f.periodFrom, periodTo: f.periodTo, paidDate: f.paidDate,
      tpManual: f.tpManual, taxPoint: f.tpManual ? f.taxPoint : '', currency: c3, amount: parseAmount(f.amount), vatRate: Number(f.vatRate),
      rate: c3 === 'PLN' ? null : parseAmount(f.rate), rateTable: f.rateTable.trim(), status: f.status, note: f.note.trim(),
      ...(vatCharged != null && initial?.vatCharged == null ? { vatCharged } : {}),
    };
  };
  const asInv = () => formInv(v);

  /** Wpisuje odczytane pola tylko tam, gdzie formularz jest pusty; usługę wybiera, gdy nie była wybrana. */
  const applyPdf = (r: ExtractResponse) => {
    if (r.message) return setPdfNote({ ok: false, text: r.message });
    const now = getValues();
    // Kilka pasujących usług: nie zgadujemy („Inne” byłoby błędem) – użytkownik wybiera z listy.
    const ambiguous = !now.serviceId && !r.serviceId && (r.serviceCandidates?.length ?? 0) > 1;
    const serviceId = now.serviceId || r.serviceId || (ambiguous ? '' : OTHER);
    if (serviceId !== now.serviceId) setValue('serviceId', serviceId);
    const ex = ambiguous ? { ...r.extracted, vendor: undefined, country: undefined } : r.extracted;
    // Szkic z Gmaila, jeszcze nie zapisany przez użytkownika: data maila i waluta usługi to wartości zastępcze.
    const replace = initial?.status === 'draft'
      ? (['issueDate', 'currency'] as const).filter((k) => !extracted.includes(k) && now[k] === (initial[k] || '')) : [];
    const { inv, filled } = applyExtracted(formInv({ ...now, serviceId }), ex, { services: data.services, replace: [...replace] });
    for (const k of filled) {
      if (k === 'vatCharged') setVatCharged(inv.vatCharged);
      else if ((PDF_FIELDS as readonly string[]).includes(k)) {
        const val = inv[k as PdfField];
        setValue(k as PdfField, k === 'amount' ? num2(Number(val)).replace(/\s/g, '') : String(val ?? ''), { shouldDirty: true });
      }
    }
    setExtracted((p) => [...new Set([...p, ...filled])]);
    const names = [...new Set(filled.map((k) => PDF_LABEL[k]).filter(Boolean))];
    if (ambiguous) {
      const list = r.serviceCandidates!.map((sid) => data.services[sid]?.name || sid).join(', ');
      return setPdfNote({ ok: false, text: `Pasuje kilka usług: ${list} – wybierz właściwą.` });
    }
    setPdfNote(names.length
      ? { ok: true, text: `Odczytano z PDF: ${names.join(', ')} – sprawdź z plikiem.` }
      : { ok: true, text: 'Odczytano PDF, ale wszystkie pola były już wypełnione.' });
  };
  const readPdf = async (p: Promise<ExtractResponse>) => {
    setReading(true);
    setPdfNote(null);
    try {
      applyPdf(await p);
    } catch (e) {
      setPdfNote({ ok: false, text: (e as Error).message });
    } finally {
      setReading(false);
    }
  };
  const pick = (f: File | null) => {
    setFile(f);
    if (f && isPdf(f)) void readPdf(api.extractUpload(f));
  };
  const fromPdf = (k: string, base = '') => [base, extracted.includes(k) ? 'from-pdf' : ''].filter(Boolean).join(' ') || undefined;
  const tp = taxPoint(asInv());
  const rd = tp ? rateDateFor(tp.date) : null;

  // Kurs NBP: z pamięci podręcznej albo z API NBP przez serwer.
  const lastKey = useRef<string | null>(null);
  useEffect(() => {
    if (cur.length !== 3 || cur === 'PLN' || !rd) return;
    const key = `${cur}_${rd}`;
    if (lastKey.current === key) return;
    const first = lastKey.current === null;
    lastKey.current = key;
    if (first && getValues('rate') && initial?.rateDate === rd) return;
    const cached = data.rates[key];
    if (cached) {
      setValue('rate', fmtRate(cached.mid));
      setValue('rateTable', cached.table || '');
      setRateState('');
      return;
    }
    setValue('rate', '');
    setValue('rateTable', '');
    setRateState('Pobieram kurs z NBP…');
    api.nbp(cur, rd)
      .then((r) => {
        if (lastKey.current !== key) return;
        setValue('rate', fmtRate(r.mid));
        setValue('rateTable', r.table || '');
        setRateState('');
      })
      .catch((e: Error) => lastKey.current === key && setRateState(e.message));
  }, [cur, rd]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = { ...asInv(), rateDate: rd || '' };
  const c = calcInv(preview.status === 'draft' ? { ...preview, status: 'new' } : preview, svc, data.rates);
  const warn: string[] = [];
  if ((svc || other) && c.domestic) warn.push('Polski dostawca: to zakup krajowy, nie import usług.');
  if (tp?.weak) warn.push('Brakuje daty zapłaty i okresu usługi. Uzupełnij je, bo od nich zależy kurs NBP i miesiąc rozliczenia.');
  if (svc && !svc.country) warn.push('Usługa nie ma kraju dostawcy. Uzupełnij go w danych usługi.');
  if (other && !v.country.trim()) warn.push('Wpisz kraj dostawcy – biuro potrzebuje go w zestawieniu.');

  const submit = handleSubmit(async () => {
    const inv = asInv();
    // Po zapisie pola są sprawdzone – znika lista pól z PDF.
    delete inv.extracted;
    if (inv.amount == null) return toast('Podaj kwotę netto.');
    if (!/^[A-Z]{3}$/.test(cur)) return toast('Podaj walutę jako trzy litery, np. USD.');
    if (cur !== 'PLN' && inv.rate == null) return toast('Uzupełnij kurs NBP.');
    if (inv.status === 'draft') inv.status = 'new';
    inv.rateDate = inv.rate && rd ? rd : '';
    const now = new Date().toISOString();
    inv.updatedAt = now;
    inv.createdAt ||= now;
    if (inv.status === 'sent' && !inv.sentAt) inv.sentAt = now;
    const docId = id || 'inv-' + Date.now().toString(36);
    setBusy(true);
    try {
      await api.put('invoices', docId, inv);
      if (inv.rate && rd && cur !== 'PLN' && !data.rates[`${cur}_${rd}`]) {
        await api.put('rates', `${cur}_${rd}`, { cur, date: rd, mid: inv.rate, table: inv.rateTable || '' }).catch(() => {});
      }
      if (file) {
        try {
          await api.upload(docId, file);
          toast('Zapisano fakturę i plik na Dysku');
        } catch (e) {
          toast(`Faktura zapisana, ale plik nie: ${(e as Error).message}`);
        }
      } else toast('Zapisano fakturę');
      await qc.invalidateQueries({ queryKey: ['data'] });
      onClose();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  });

  const nbpLink = rd && cur.length === 3 && cur !== 'PLN'
    ? `https://api.nbp.pl/api/exchangerates/rates/a/${cur.toLowerCase()}/${rd}/?format=json` : null;

  return (
    <>
      <Sheet title={id ? (initial?.status === 'draft' ? 'Faktura do uzupełnienia' : 'Faktura') : 'Nowa faktura'} onClose={onClose}
        footer={<>
          {id ? <DeleteButton onConfirm={() => { del.mutate({ col: 'invoices', id }); onClose(); }} /> : <span />}
          <div className="r"><button className="btn" onClick={onClose}>Anuluj</button><button className="btn pri" disabled={busy} onClick={submit}>{busy ? 'Zapisuję…' : 'Zapisz'}</button></div>
        </>}>
        <form className="fgrid" onSubmit={submit}>
          {initial?.status === 'draft' && <div className="note hi full">{initial.gmailId ? 'Faktura z Gmaila' : 'Faktura z pliku'}: plik jest na Dysku. Uzupełnij kwotę, daty i walutę z PDF-a.</div>}
          {(initial?.vatCharged ?? 0) > 0 && <div className="note warn full">Na fakturze naliczono podatek. Dostawca prawdopodobnie nie ma Twojego numeru VAT-UE. Do odwrotnego obciążenia bierzesz kwotę netto.</div>}
          <div className="fld full">
            <label htmlFor="i-file">Plik faktury</label>
            {initial?.fileLink && (
              <div className="copyrow" style={{ marginBottom: 6 }}>
                <a className="btn sm" href={initial.fileLink} target="_blank" rel="noreferrer"><Icon.ext />Otwórz na Dysku</a>
                {initial.fileId && !file && id && <button type="button" className="btn sm" disabled={reading} onClick={() => void readPdf(api.extractSaved(id))}>Odczytaj z pliku</button>}
                <span className="muted" style={{ fontSize: 13 }}>{initial.fileName}</span>
              </div>
            )}
            <FileDrop id="i-file" label="Przeciągnij PDF faktury albo wybierz plik" onFiles={(f) => pick(f[0] || null)}>
              {file && <span className="drop-file">{file.name}</span>}
            </FileDrop>
            <div className="hint">
              {reading ? 'Odczytuję dane z PDF…'
                : `Z PDF odczytam numer, daty, okres, walutę i kwotę. ${me?.google.drive ? 'Plik trafi na Twój Dysk do folderu miesiąca.' : 'Połącz Dysk Google w zakładce Firma, żeby zapisywać pliki.'}`}
            </div>
            {pdfNote && <div className={`note ${pdfNote.ok ? 'hi' : 'warn'}`} style={{ marginTop: 8 }}>{pdfNote.text}</div>}
          </div>
          <div className="fld full">
            <label htmlFor="i-svc">Usługa</label>
            <div className="copyrow">
              <select id="i-svc" {...register('serviceId', { required: true })}>
                <option value="">Wybierz usługę…</option>
                <option value={OTHER}>Inne – bez usługi</option>
                {Object.entries(data.services)
                  .filter(([k, s]) => s.active !== false || k === initial?.serviceId)
                  .sort((a, b) => a[1].name.localeCompare(b[1].name, 'pl'))
                  .map(([k, s]) => <option key={k} value={k}>{s.name}{s.country ? ` (${s.country})` : ''}</option>)}
              </select>
              <button type="button" className="btn sm" onClick={() => setNewService(true)}><Icon.plus />Nowa</button>
            </div>
            {errors.serviceId && <div className="hint err">Wybierz usługę albo „Inne”.</div>}
          </div>
          {other && <>
            <div className="fld">
              <label htmlFor="i-vendor">Dostawca</label>
              <input id="i-vendor" autoComplete="off" placeholder="Nazwa firmy z faktury" className={fromPdf('vendor')} {...register('vendor', { validate: (x) => !!x.trim() })} />
              {errors.vendor && <div className="hint err">Podaj nazwę dostawcy z faktury.</div>}
            </div>
            <div className="fld">
              <label htmlFor="i-country">Kraj</label>
              <input id="i-country" maxLength={2} placeholder="DE" className={fromPdf('country', 'upper')} autoComplete="off"
                {...register('country', { validate: (x) => !x.trim() || /^[A-Za-z]{2}$/.test(x.trim()) })} />
              {errors.country ? <div className="hint err">Kraj jako dwie litery, np. DE.</div> : <div className="hint">Dwie litery, np. DE, IE, US.</div>}
            </div>
          </>}
          <div className="fld"><label htmlFor="i-num">Numer faktury</label><input id="i-num" autoComplete="off" className={fromPdf('number')} {...register('number')} /></div>
          <div className="fld">
            <label htmlFor="i-issue">Data wystawienia</label>
            <input id="i-issue" type="date" className={fromPdf('issueDate')} {...register('issueDate', { required: true })} />
            {errors.issueDate && <div className="hint err">Podaj datę wystawienia.</div>}
          </div>
          <div className="fld"><label htmlFor="i-from">Okres usługi od</label><input id="i-from" type="date" className={fromPdf('periodFrom')} {...register('periodFrom')} /></div>
          <div className="fld"><label htmlFor="i-to">Okres usługi do</label><input id="i-to" type="date" className={fromPdf('periodTo')} {...register('periodTo')} /></div>
          <div className="fld"><label htmlFor="i-paid">Data zapłaty</label><input id="i-paid" type="date" className={fromPdf('paidDate')} {...register('paidDate')} /></div>
          <div className="fld"><label htmlFor="i-cur">Waluta</label><input id="i-cur" maxLength={3} placeholder="USD" className={fromPdf('currency', 'upper')} autoComplete="off" {...register('currency')} /></div>
          <div className="fld"><label htmlFor="i-amt">Kwota netto</label><input id="i-amt" inputMode="decimal" autoComplete="off" className={fromPdf('amount')} {...register('amount')} /></div>
          <div className="fld">
            <label htmlFor="i-rate">Kurs NBP</label>
            <input id="i-rate" inputMode="decimal" autoComplete="off" disabled={cur === 'PLN'} {...register('rate')} />
            <div className="hint">
              {cur === 'PLN' ? 'Faktura w złotych, bez przeliczania.'
                : rd ? <>Kurs z {fmtD(rd)}{rateState ? `, ${rateState}` : ''}.{nbpLink && <>{' '}<a href={nbpLink} target="_blank" rel="noreferrer">Tabela NBP</a></>}</>
                  : 'Uzupełnij datę zapłaty albo okres usługi i walutę.'}
            </div>
          </div>
          <div className="fld"><label htmlFor="i-table">Nr tabeli NBP</label><input id="i-table" autoComplete="off" {...register('rateTable')} /></div>
          <div className="fld">
            <label htmlFor="i-status">Status</label>
            <select id="i-status" {...register('status')}>
              {Object.entries(STATUS_LABEL).filter(([k]) => k !== 'draft' || initial?.status === 'draft').map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="fld full"><label htmlFor="i-note">Notatka</label><input id="i-note" autoComplete="off" {...register('note')} /></div>
          <div className="preview full">
            <h4>Kwota w złotych</h4>
            <dl className="kv">
              <dt>Miesiąc rozliczenia</dt><dd>{c.month ? fmtYM(c.month) : '—'}</dd>
              {cur !== 'PLN' && <><dt>Kurs NBP</dt><dd>{rd ? `z ${fmtD(rd)}` : '—'}</dd></>}
              <dt>Netto w złotych</dt><dd>{money(c.net)}</dd>
            </dl>
            {warn.length > 0 && <div className="note warn" style={{ marginTop: 10 }}>{warn.map((w) => <div key={w}>{w}</div>)}</div>}
          </div>
        </form>
      </Sheet>
      {newService && <ServiceForm onClose={() => setNewService(false)} />}
    </>
  );
}
